import * as React from 'react'
import { PLANT_ROBOTICS_NODE_QUERY_PARAM, plantHref } from '@atbash-labs/plantview-links'
import {
  usePlantSessionPaint,
  type PlantRobotTreeNode
} from '@axilon-apps/domains-ontology-ui-state'
import {
  PlantTreeNav,
  buildTreeByParentId,
  type ParentIdTreeRow,
  type PlantTreeNavGhost,
  type PlantTreeNavNode
} from './PlantTreeNav'
import { collectUnplacedGhosts } from './session-mark-ui'
import { robotIconFor } from './robot-icon'

/** Child kinds that ghost under a RobotController / RobotArm in the tree. */
const ROBOT_CHILD_LABELS = new Set(['RobotArm', 'RobotJob'])

/**
 * Robotics tab adapter over {@link PlantTreeNav}. Maps the flat
 * `useGetPlantRoboticsTreeQuery` node list (Controller → Arm → Job)
 * onto the shared tree rail: kind glyphs, `jobCount` badges on
 * container rows (jobs themselves carry none), and an amber "review"
 * pill on nodes flagged `needsReview`. Fully expanded by default so
 * the fleet stays scannable. Selection is the `?node=<id>` query param.
 *
 * When a session is painting, rows carry word-pill marks and deleted
 * children render as ghost rows under their parent (or at the root when
 * unplaced).
 */
export interface RoboticsTreeNavProps {
  nodes: PlantRobotTreeNode[]
  selectedId?: string
  isLoading?: boolean
  className?: string
}

export function RoboticsTreeNav({
  nodes,
  selectedId,
  isLoading,
  className
}: RoboticsTreeNavProps): React.ReactElement {
  const { active, filter, markFor, ghostsByContainerId, markById } = usePlantSessionPaint()
  const paintOn = active && filter.enabled

  const navNodes = React.useMemo(() => {
    const toNavNode = (row: ParentIdTreeRow<PlantRobotTreeNode>): PlantTreeNavNode => {
      const id = row.node.id
      const ghosts: PlantTreeNavGhost[] = []
      if (paintOn) {
        for (const ghost of ghostsByContainerId.get(id) ?? []) {
          if (!ghost.labels.some((label) => ROBOT_CHILD_LABELS.has(label))) continue
          const view = markFor(ghost.id)
          if (view !== 'deleted' && view !== 'dimmed') continue
          ghosts.push({ id: ghost.id, name: ghost.name ?? ghost.id, view })
        }
      }
      return {
        id,
        href: plantHref(row.node.id, row.node.kind, row.node),
        label: row.node.name,
        icon: robotIconFor(row.node.kind),
        count: row.node.kind !== 'RobotJob' ? row.node.jobCount : undefined,
        pill: row.node.needsReview ? 'review' : undefined,
        mark: paintOn ? markFor(id) : undefined,
        ghosts: ghosts.length > 0 ? ghosts : undefined,
        groups: [{ nodes: row.children.map(toNavNode) }]
      }
    }
    return buildTreeByParentId(
      nodes,
      (node) => node.id,
      (node) => node.parentId
    ).map(toNavNode)
  }, [ghostsByContainerId, markFor, nodes, paintOn])

  const rootGhosts = React.useMemo((): PlantTreeNavGhost[] | undefined => {
    if (!paintOn) return undefined
    const liveIds = new Set(nodes.map((node) => node.id))
    const unplaced = collectUnplacedGhosts(markById, ghostsByContainerId, markFor, {
      label: 'RobotController',
      liveIds
    })
    if (unplaced.length === 0) return undefined
    return unplaced.map((ghost) => ({
      id: ghost.id,
      name: ghost.name ?? ghost.id,
      view: ghost.view
    }))
  }, [ghostsByContainerId, markById, markFor, nodes, paintOn])

  return (
    <PlantTreeNav
      heading="Robotics"
      nodes={navNodes}
      selectParam={PLANT_ROBOTICS_NODE_QUERY_PARAM}
      selectedId={selectedId}
      isLoading={isLoading}
      loadingText="Loading robotics…"
      emptyTitle="No robot controllers"
      emptyHint="Ingest a Yaskawa, FANUC, or Yamaha controller backup to populate robot controllers, arms, and jobs."
      defaultExpanded
      rootGhosts={rootGhosts}
      className={className}
    />
  )
}
